<!-- SuspendMemberModal.vue - Updated to use cbhiNumber -->
<template>
  <div class="modal fade" id="suspendMemberModal" tabindex="-1" role="dialog"
       aria-labelledby="suspendMemberModalLabel" aria-hidden="true"
       data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header bg-warning">
          <h5 class="modal-title text-white" id="suspendMemberModalLabel">
            <i class="bx bx-pause-circle me-2"></i>
            Suspend/Archive Member
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"
                  aria-label="Close" :disabled="processing"></button>
        </div>

        <div class="modal-body">
          <!-- Error Alert Display -->
          <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <div class="d-flex align-items-start">
              <i class="bx bx-error-circle fs-4 me-2"></i>
              <div class="flex-grow-1">
                <strong>Error:</strong> {{ errorMessage }}
              </div>
              <button type="button" class="btn-close" @click="clearError" aria-label="Close"></button>
            </div>
          </div>

          <!-- Success Alert Display -->
          <div v-if="successMessage" class="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <div class="d-flex align-items-start">
              <i class="bx bx-check-circle fs-4 me-2"></i>
              <div class="flex-grow-1">
                {{ successMessage }}
              </div>
              <button type="button" class="btn-close" @click="clearSuccess" aria-label="Close"></button>
            </div>
          </div>

          <!-- Member Information Summary -->
          <div class="alert alert-warning mb-4">
            <div class="d-flex align-items-start">
              <i class="bx bx-info-circle fs-3 me-3"></i>
              <div class="flex-grow-1">
                <h6 class="alert-heading mb-2">
                  You are about to suspend member:
                  <strong>{{ memberFullName }}</strong>
                </h6>
                <p class="mb-2">
                  <strong>CBHI Number:</strong> {{ cbhiNumber }}<br>
                  <strong>Current Status:</strong>
                  <span class="badge bg-success">{{ capitalizeFirst(memberInfo.status) }}</span>
                </p>
                <hr class="my-2">
                <p class="mb-0 small">
                  <i class="bx bx-exclamation-triangle me-1"></i>
                  <strong>Important:</strong> Suspending this member will:
                </p>
                <ul class="mb-0 small">
                  <li>Change member status from Active to Suspended</li>
                  <li>Prevent new claims from being submitted</li>
                  <li>Suspend all {{ beneficiaryCount }} beneficiaries</li>
                  <li>Halt premium collection until reactivation</li>
                  <li>Maintain all historical records for audit purposes</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Suspension Type Selection -->
          <div class="mb-4">
            <label class="form-label fw-bold">
              <i class="bx bx-category me-1"></i>
              Suspension Type <span class="text-danger">*</span>
            </label>
            <div class="row">
              <div class="col-md-6 mb-2">
                <div class="form-check card p-3"
                     :class="{ 'border-primary bg-light': suspensionType === 'temporary' }">
                  <input class="form-check-input" type="radio"
                         name="suspensionType" id="temporarySuspension"
                         value="temporary" v-model="suspensionType">
                  <label class="form-check-label ms-2" for="temporarySuspension">
                    <strong>Temporary Suspension</strong>
                    <p class="mb-0 text-muted small">
                      Member can be reactivated later
                    </p>
                  </label>
                </div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="form-check card p-3"
                     :class="{ 'border-primary bg-light': suspensionType === 'permanent' }">
                  <input class="form-check-input" type="radio"
                         name="suspensionType" id="permanentArchive"
                         value="permanent" v-model="suspensionType">
                  <label class="form-check-label ms-2" for="permanentArchive">
                    <strong>Permanent Archive</strong>
                    <p class="mb-0 text-muted small">
                      Member record will be archived
                    </p>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Suspension Category -->
          <div class="mb-4">
            <label for="suspensionCategory" class="form-label fw-bold">
              <i class="bx bx-list-ul me-1"></i>
              Reason Category <span class="text-danger">*</span>
            </label>
            <select class="form-select" id="suspensionCategory"
                    v-model="suspensionCategory" required>
              <option value="">-- Select Reason Category --</option>
              <option value="non_payment">Non-Payment of Premiums</option>
              <option value="fraud">Suspected Fraud or Misuse</option>
              <option value="deceased">Member Deceased</option>
              <option value="relocated">Member Relocated</option>
              <option value="duplicate">Duplicate Registration</option>
              <option value="request">Member Request</option>
              <option value="eligibility">No Longer Eligible</option>
              <option value="violation">Policy Violation</option>
              <option value="administrative">Administrative Action</option>
              <option value="other">Other Reason</option>
            </select>
            <div v-if="!suspensionCategory && showValidation" class="invalid-feedback d-block">
              Please select a reason category
            </div>
          </div>

          <!-- Expected Reactivation Date (for temporary suspension) -->
          <div class="mb-4" v-if="suspensionType === 'temporary'">
            <label for="expectedReactivationDate" class="form-label fw-bold">
              <i class="bx bx-calendar me-1"></i>
              Expected Reactivation Date (Optional)
            </label>
            <input type="date" class="form-control" id="expectedReactivationDate"
                   v-model="expectedReactivationDate"
                   :min="minDate">
            <small class="text-muted">
              Leave blank if reactivation date is unknown
            </small>
          </div>

          <!-- Detailed Reason -->
          <div class="mb-4">
            <label for="suspensionReason" class="form-label fw-bold">
              <i class="bx bx-edit me-1"></i>
              Detailed Reason <span class="text-danger">*</span>
            </label>
            <textarea class="form-control" id="suspensionReason" rows="4"
                      v-model="suspensionReason"
                      @input="updateCharCount"
                      placeholder="Please provide a detailed explanation for suspending this member. Include relevant dates, incidents, or circumstances that led to this decision."
                      :class="{ 'is-invalid': showValidation && !isReasonValid }"
                      required></textarea>
            <div class="d-flex justify-content-between mt-1">
              <small class="text-muted">
                Minimum {{ minReasonLength }} characters required
              </small>
              <small :class="charCountClass">
                {{ charCount }} / {{ minReasonLength }}
              </small>
            </div>
            <div v-if="showValidation && !isReasonValid" class="invalid-feedback d-block">
              Please provide a detailed reason (minimum {{ minReasonLength }} characters)
            </div>
          </div>

          <!-- Supporting Documents (Optional) -->
          <div class="mb-4">
            <label class="form-label fw-bold">
              <i class="bx bx-paperclip me-1"></i>
              Supporting Documents (Optional)
            </label>
            <input type="file" class="form-control"
                   @change="handleFileUpload"
                   accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                   multiple>
            <small class="text-muted">
              Upload any supporting documents (PDF, DOC, DOCX, JPG, PNG)
            </small>
            <div v-if="uploadedFiles.length > 0" class="mt-2">
              <div v-for="(file, index) in uploadedFiles" :key="index"
                   class="badge bg-secondary me-2 mb-1">
                {{ file.name }}
                <i class="bx bx-x ms-1 cursor-pointer" @click="removeFile(index)"></i>
              </div>
            </div>
          </div>

          <!-- Notify Member Checkbox -->
          <div class="form-check mb-4">
            <input class="form-check-input" type="checkbox" id="notifyMember"
                   v-model="notifyMember">
            <label class="form-check-label" for="notifyMember">
              <i class="bx bx-envelope me-1"></i>
              Send notification to member about suspension
            </label>
          </div>

          <!-- Admin Authorization -->
          <div class="card bg-light mb-4">
            <div class="card-body">
              <h6 class="card-title mb-3">
                <i class="bx bx-shield-security me-1"></i>
                Authorization Required
              </h6>
              <div class="mb-3">
                <label for="adminPassword" class="form-label">
                  Enter your password to confirm <span class="text-danger">*</span>
                </label>
                <input type="password" class="form-control" id="adminPassword"
                       v-model="adminPassword"
                       placeholder="Enter your password"
                       :class="{ 'is-invalid': showValidation && !adminPassword }">
                <div v-if="showValidation && !adminPassword" class="invalid-feedback">
                  Password is required for authorization
                </div>
              </div>
            </div>
          </div>

          <!-- Final Confirmation Checkbox -->
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="confirmSuspension"
                   v-model="confirmSuspension">
            <label class="form-check-label text-danger fw-bold" for="confirmSuspension">
              I understand the implications and confirm the suspension of this member
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal"
                  :disabled="processing">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button type="button" class="btn btn-warning" @click="validateAndSubmit"
                  :disabled="processing || !confirmSuspension">
            <span v-if="!processing">
              <i class="bx bx-pause-circle me-1"></i>
              {{ suspensionType === 'permanent' ? 'Archive Member' : 'Suspend Member' }}
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm me-1" role="status"></span>
              Processing...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "SuspendMemberModal",
  props: {
    // Changed from memberId to cbhiNumber as primary identifier
    cbhiNumber: {
      type: String,
      required: true
    },
    memberInfo: {
      type: Object,
      required: true
    },
    beneficiaryCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      suspensionType: 'temporary',
      suspensionCategory: '',
      suspensionReason: '',
      expectedReactivationDate: '',
      adminPassword: '',
      confirmSuspension: false,
      notifyMember: true,
      uploadedFiles: [],
      processing: false,
      showValidation: false,
      charCount: 0,
      minReasonLength: 50,
      minDate: '',
      errorMessage: '',
      successMessage: ''
    };
  },
  computed: {
    memberFullName() {
      if (!this.memberInfo) return 'Unknown Member';

      // Handle different possible property formats
      const firstName = this.memberInfo.first_name || this.memberInfo.firstName || '';
      const middleName = this.memberInfo.middle_name || this.memberInfo.middleName || '';
      const surname = this.memberInfo.surname || this.memberInfo.lastName || '';

      return `${firstName} ${middleName} ${surname}`.trim();
    },
    isReasonValid() {
      return this.suspensionReason.trim().length >= this.minReasonLength;
    },
    charCountClass() {
      if (this.charCount >= this.minReasonLength) {
        return 'text-success';
      } else if (this.charCount > this.minReasonLength * 0.6) {
        return 'text-warning';
      }
      return 'text-danger';
    }
  },
  mounted() {
    // Set minimum date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow.toISOString().split('T')[0];
  },
  methods: {
    openModal() {
      this.resetForm();
      const modal = new bootstrap.Modal(document.getElementById('suspendMemberModal'));
      modal.show();
    },

    closeModal() {
      if (this.processing) return;

      const modal = bootstrap.Modal.getInstance(document.getElementById('suspendMemberModal'));
      if (modal) {
        modal.hide();
      }
      this.resetForm();
    },

    resetForm() {
      this.suspensionType = 'temporary';
      this.suspensionCategory = '';
      this.suspensionReason = '';
      this.expectedReactivationDate = '';
      this.adminPassword = '';
      this.confirmSuspension = false;
      this.notifyMember = true;
      this.uploadedFiles = [];
      this.processing = false;
      this.showValidation = false;
      this.charCount = 0;
      this.errorMessage = '';
      this.successMessage = '';
    },

    updateCharCount() {
      this.charCount = this.suspensionReason.trim().length;
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      this.uploadedFiles = [...this.uploadedFiles, ...files];
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    showError(message) {
      this.errorMessage = message;
      this.successMessage = '';

      // Auto-scroll to top of modal to show error
      const modalBody = document.querySelector('#suspendMemberModal .modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    },

    showSuccess(message) {
      this.successMessage = message;
      this.errorMessage = '';

      // Auto-scroll to top of modal to show success
      const modalBody = document.querySelector('#suspendMemberModal .modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    },

    clearError() {
      this.errorMessage = '';
    },

    clearSuccess() {
      this.successMessage = '';
    },

    validateAndSubmit() {
      this.showValidation = true;
      this.clearError();
      this.clearSuccess();

      // Validation
      if (!this.suspensionCategory) {
        this.showError('Please select a reason category');
        this.$emit('suspension-error', {
          message: 'Please select a reason category'
        });
        return;
      }

      if (!this.isReasonValid) {
        this.showError(`Please provide a detailed reason (minimum ${this.minReasonLength} characters)`);
        this.$emit('suspension-error', {
          message: `Please provide a detailed reason (minimum ${this.minReasonLength} characters)`
        });
        return;
      }

      if (!this.adminPassword) {
        this.showError('Please enter your password for authorization');
        this.$emit('suspension-error', {
          message: 'Please enter your password for authorization'
        });
        return;
      }

      if (!this.confirmSuspension) {
        this.showError('Please confirm the suspension by checking the confirmation box');
        this.$emit('suspension-error', {
          message: 'Please confirm the suspension by checking the confirmation box'
        });
        return;
      }

      // Show confirmation dialog
      const actionText = this.suspensionType === 'permanent' ? 'archive' : 'suspend';
      const confirmMessage = `Are you absolutely sure you want to ${actionText} ${this.memberFullName}? This action will affect the member and all ${this.beneficiaryCount} beneficiaries.`;

      if (confirm(confirmMessage)) {
        this.submitSuspension();
      }
    },

    async submitSuspension() {
      this.processing = true;
      this.clearError();
      this.clearSuccess();

      // Using cbhiNumber instead of memberId
      const data = {
        function: "suspendMember",
        cbhiNumber: this.cbhiNumber,  // Changed from memberId
        suspensionType: this.suspensionType,
        suspensionCategory: this.suspensionCategory,
        suspensionReason: this.suspensionReason.trim(),
        expectedReactivationDate: this.expectedReactivationDate || null,
        adminPassword: this.adminPassword,
        notifyMember: this.notifyMember,
        supportingDocuments: this.uploadedFiles.map(f => f.name)
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data && res.data.success) {
          this.showSuccess(res.data.message || 'Member suspended successfully');

          this.$emit('member-suspended', {
            success: true,
            message: res.data.message || 'Member suspended successfully',
            data: res.data.data
          });

          // Close modal after successful suspension with a delay to show success message
          setTimeout(() => {
            const modalElement = document.getElementById('suspendMemberModal');
            const modal = bootstrap.Modal.getInstance(modalElement);
            if (modal) {
              modal.hide();
            }

            // Reset form after closing
            setTimeout(() => {
              this.resetForm();
            }, 300);
          }, 2000);
        } else {
          const errorMsg = (res.data && res.data.message) || 'Failed to suspend member';
          this.showError(errorMsg);
          this.$emit('suspension-error', {
            message: errorMsg
          });
          this.processing = false;
        }
      } catch (error) {
        console.error('Error suspending member:', error);
        const errorMsg = error.message || 'An error occurred while suspending the member';
        this.showError(errorMsg);
        this.$emit('suspension-error', {
          message: errorMsg
        });
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-header.bg-warning {
  background-color: #ffc107 !important;
}

.btn-close-white {
  filter: invert(1);
}

.form-check.card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.form-check.card:hover {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-check.card.border-primary {
  border-width: 2px;
}

.cursor-pointer {
  cursor: pointer;
}

.invalid-feedback {
  display: block;
}

textarea {
  resize: vertical;
  min-height: 100px;
}

.badge i.bx-x:hover {
  color: #dc3545;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.alert-success {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.card.bg-light {
  background-color: #f8f9fa !important;
}

.alert {
  position: relative;
  z-index: 1;
}

.modal-body {
  scroll-behavior: smooth;
}
</style>